import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaPlus, FaTrash, FaSave, FaEdit, FaCode, FaDatabase } from 'react-icons/fa';
import { db } from '../../firebase';
import { doc, getDoc, setDoc, collection, getDocs } from 'firebase/firestore';

const DynamicFieldManager = () => {
  const [schemas, setSchemas] = useState({});
  const [selectedCollection, setSelectedCollection] = useState('projects');
  const [newFieldName, setNewFieldName] = useState('');
  const [newFieldType, setNewFieldType] = useState('string');
  const [newFieldRequired, setNewFieldRequired] = useState(false);
  const [newFieldDefault, setNewFieldDefault] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewData, setPreviewData] = useState(null);

  const fieldTypes = [
    { value: 'string', label: 'Text' },
    { value: 'number', label: 'Number' },
    { value: 'boolean', label: 'True/False' },
    { value: 'array', label: 'List' },
    { value: 'object', label: 'Object' },
    { value: 'url', label: 'URL' },
    { value: 'email', label: 'Email' },
    { value: 'date', label: 'Date' },
    { value: 'richtext', label: 'Rich Text' },
    { value: 'image', label: 'Image URL' },
    { value: 'color', label: 'Color' },
  ];

  const collections = [
    'projects',
    'profile',
    'content',
    'skills',
    'experience',
    'education',
    'certifications',
  ];

  useEffect(() => {
    fetchSchemas();
  }, []);

  const fetchSchemas = async () => {
    try {
      setLoading(true);
      const docRef = doc(db, 'fieldSchema', 'collections');
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        setSchemas(docSnap.data());
      } else {
        // Initialize with default schemas
        const defaultSchemas = {
          projects: {
            title: { type: 'string', required: true, label: 'Project Title' },
            description: { type: 'string', required: true, label: 'Description' },
            image: { type: 'image', required: false, label: 'Project Image' },
            tags: { type: 'array', required: false, label: 'Technologies' },
            category: { type: 'string', required: true, label: 'Category' },
            github: { type: 'url', required: false, label: 'GitHub Link' },
            live: { type: 'url', required: false, label: 'Live Demo Link' },
            featured: { type: 'boolean', required: false, label: 'Featured Project' },
          },
          profile: {
            name: { type: 'string', required: true, label: 'Full Name' },
            email: { type: 'email', required: true, label: 'Email Address' },
            bio: { type: 'richtext', required: false, label: 'Bio' },
            tagline: { type: 'string', required: false, label: 'Tagline' },
          },
          content: {
            title: { type: 'string', required: true, label: 'Title' },
            subtitle: { type: 'string', required: false, label: 'Subtitle' },
            body: { type: 'richtext', required: false, label: 'Content' },
          },
        };
        setSchemas(defaultSchemas);
      }
    } catch (error) {
      console.error('Error fetching schemas:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddField = () => {
    if (!newFieldName.trim()) {
      alert('Please enter a field name');
      return;
    }

    const fieldKey = newFieldName.toLowerCase().replace(/\s+/g, '_');
    
    setSchemas(prev => ({
      ...prev,
      [selectedCollection]: {
        ...prev[selectedCollection],
        [fieldKey]: {
          type: newFieldType,
          required: newFieldRequired,
          label: newFieldName,
          default: newFieldDefault || undefined,
        }
      }
    }));

    // Reset form
    setNewFieldName('');
    setNewFieldType('string');
    setNewFieldRequired(false);
    setNewFieldDefault('');
  };

  const handleRemoveField = (fieldKey) => {
    if (!confirm(`Are you sure you want to remove the field "${fieldKey}"?`)) return;

    setSchemas(prev => {
      const updated = { ...prev };
      if (updated[selectedCollection]) {
        delete updated[selectedCollection][fieldKey];
      }
      return updated;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await setDoc(doc(db, 'fieldSchema', 'collections'), {
        ...schemas,
        updatedAt: new Date().toISOString()
      });
      alert('Field schemas saved successfully!');
    } catch (error) {
      console.error('Error saving schemas:', error);
      alert('Error saving schemas. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = async () => {
    try {
      const collectionRef = collection(db, selectedCollection);
      const snapshot = await getDocs(collectionRef);
      
      if (!snapshot.empty) {
        const firstDoc = snapshot.docs[0];
        setPreviewData({
          id: firstDoc.id,
          data: firstDoc.data()
        });
      } else {
        setPreviewData(null);
        alert('No documents found in this collection');
      }
    } catch (error) {
      console.error('Error fetching preview:', error);
    }
  };

  const generateCodeSnippet = () => {
    const schema = schemas[selectedCollection] || {};
    const code = `// ${selectedCollection} Schema
const ${selectedCollection}Schema = {
${Object.entries(schema).map(([key, field]) => 
  `  ${key}: {
    type: '${field.type}',
    required: ${field.required},
    label: '${field.label}'${field.default ? `,\n    default: '${field.default}'` : ''}
  }`
).join(',\n')}
};`;
    return code;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-white">Dynamic Field Manager</h2>
        <div className="flex gap-2">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handlePreview}
            className="flex items-center gap-2 bg-gray-700 text-white px-4 py-2 rounded-lg hover:bg-gray-600"
          >
            <FaDatabase />
            Preview Data
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 bg-gradient-to-r from-violet-500 to-purple-500 text-white px-6 py-2 rounded-lg hover:shadow-lg transition-all disabled:opacity-50"
          >
            <FaSave />
            {saving ? 'Saving...' : 'Save Schema'}
          </motion.button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Panel: Collection Selector */}
        <div className="lg:col-span-1 space-y-4">
          <div className="bg-gray-800 p-4 rounded-lg">
            <h3 className="text-lg font-semibold text-white mb-4">Collections</h3>
            <div className="space-y-2">
              {collections.map((collection) => (
                <button
                  key={collection}
                  onClick={() => setSelectedCollection(collection)}
                  className={`w-full text-left px-4 py-3 rounded-lg transition-all ${
                    selectedCollection === collection
                      ? 'bg-violet-500 text-white'
                      : 'bg-gray-900 text-gray-300 hover:bg-gray-700'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="capitalize">{collection}</span>
                    <span className="text-xs opacity-75">
                      {Object.keys(schemas[collection] || {}).length} fields
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Add New Field Form */}
          <div className="bg-gray-800 p-4 rounded-lg">
            <h3 className="text-lg font-semibold text-white mb-4">Add New Field</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-gray-300 text-sm mb-1">Field Name</label>
                <input
                  type="text"
                  value={newFieldName}
                  onChange={(e) => setNewFieldName(e.target.value)}
                  className="w-full bg-gray-900 text-white px-3 py-2 rounded-lg border border-gray-700 focus:border-violet-500 outline-none text-sm"
                  placeholder="e.g., Project Duration"
                />
              </div>

              <div>
                <label className="block text-gray-300 text-sm mb-1">Field Type</label>
                <select
                  value={newFieldType}
                  onChange={(e) => setNewFieldType(e.target.value)}
                  className="w-full bg-gray-900 text-white px-3 py-2 rounded-lg border border-gray-700 focus:border-violet-500 outline-none text-sm"
                >
                  {fieldTypes.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-gray-300 text-sm mb-1">Default Value</label>
                <input
                  type="text"
                  value={newFieldDefault}
                  onChange={(e) => setNewFieldDefault(e.target.value)}
                  className="w-full bg-gray-900 text-white px-3 py-2 rounded-lg border border-gray-700 focus:border-violet-500 outline-none text-sm"
                  placeholder="Optional default value"
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="required"
                  checked={newFieldRequired}
                  onChange={(e) => setNewFieldRequired(e.target.checked)}
                  className="w-4 h-4"
                />
                <label htmlFor="required" className="text-gray-300 text-sm">
                  Required field
                </label>
              </div>

              <button
                onClick={handleAddField}
                className="w-full flex items-center justify-center gap-2 bg-violet-500 text-white px-4 py-2 rounded-lg hover:bg-violet-600 transition-all"
              >
                <FaPlus />
                Add Field
              </button>
            </div>
          </div>
        </div>

        {/* Right Panel: Current Schema */}
        <div className="lg:col-span-2 space-y-4">
          <div className="bg-gray-800 p-6 rounded-lg">
            <h3 className="text-lg font-semibold text-white mb-4 capitalize">
              {selectedCollection} Schema
            </h3>

            {schemas[selectedCollection] && Object.keys(schemas[selectedCollection]).length > 0 ? (
              <div className="space-y-3">
                {Object.entries(schemas[selectedCollection]).map(([fieldKey, field]) => (
                  <motion.div
                    key={fieldKey}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-gray-900 p-4 rounded-lg border border-gray-700 hover:border-violet-500 transition-all"
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="text-white font-mono text-lg">{fieldKey}</span>
                          {field.required && (
                            <span className="text-xs bg-red-500/20 text-red-400 px-2 py-1 rounded">
                              Required
                            </span>
                          )}
                          <span className="text-xs bg-violet-500/20 text-violet-400 px-2 py-1 rounded">
                            {field.type}
                          </span>
                        </div>
                        <p className="text-gray-400 text-sm">{field.label}</p>
                        {field.default && (
                          <p className="text-gray-500 text-xs mt-1">
                            Default: {field.default}
                          </p>
                        )}
                      </div>
                      <button
                        onClick={() => handleRemoveField(fieldKey)}
                        className="text-red-400 hover:text-red-500 p-2"
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </motion.div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-gray-400">
                <FaDatabase className="mx-auto text-5xl mb-4 opacity-20" />
                <p>No fields defined yet. Add your first field!</p>
              </div>
            )}
          </div>

          {/* Code Preview */}
          <div className="bg-gray-800 p-6 rounded-lg">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <FaCode /> Schema Code
              </h3>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(generateCodeSnippet());
                  alert('Code copied to clipboard!');
                }}
                className="text-sm bg-gray-700 text-white px-3 py-1 rounded hover:bg-gray-600"
              >
                Copy
              </button>
            </div>
            <pre className="bg-gray-900 p-4 rounded-lg overflow-x-auto text-sm">
              <code className="text-gray-300 font-mono">
                {generateCodeSnippet()}
              </code>
            </pre>
          </div>

          {/* Data Preview */}
          {previewData && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-gray-800 p-6 rounded-lg"
            >
              <h3 className="text-lg font-semibold text-white mb-4">Sample Data</h3>
              <pre className="bg-gray-900 p-4 rounded-lg overflow-x-auto text-sm">
                <code className="text-gray-300 font-mono">
                  {JSON.stringify(previewData.data, null, 2)}
                </code>
              </pre>
            </motion.div>
          )}
        </div>
      </div>

      {/* Info Panel */}
      <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
        <h4 className="text-blue-400 font-semibold mb-2">💡 How to Use</h4>
        <ul className="text-gray-300 text-sm space-y-1 list-disc list-inside">
          <li>Select a collection from the left panel</li>
          <li>Add custom fields with specific types and requirements</li>
          <li>Save the schema to apply changes</li>
          <li>Use the generated code in your forms and validators</li>
          <li>Preview actual data from your Firestore collection</li>
        </ul>
      </div>
    </div>
  );
};

export default DynamicFieldManager;
